/**
 * AgencySignup — multi-step self-serve wizard.
 *
 * Steps:
 *  1. Agency basics (name, NPI optional, phone)
 *  2. Owner profile (name, email, password)
 *  3. Choose plan (Starter / Growth)
 *  4. Confirmation summary
 *  5. Welcome
 *
 * On final submit → POST /api/agencies/signup → sets auth cookie → redirects
 * into /admin?onboarding=1 to fire the existing OnboardingWizard.
 *
 * Stripe checkout is deferred to AFTER first login (Settings → Billing) so
 * we don't block agency creation on payment. Free 14-day trial.
 */
import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ArrowRight, ArrowLeft, ShieldCheck, Building2, User as UserIcon,
  Sparkles, Check, CreditCard, Mail, Lock, Phone, Hash,
} from "lucide-react";
import { AfyanexLogo } from "@/components/AfyanexLogo";
import { tagSentryUser, track } from "@/lib/sentry";

const PLANS = [
  { key: "starter", name: "Starter", price: "$180", sub: "/mo flat",
    desc: "Up to 10 active clients. AI notes, EVV, family portal." },
  { key: "growth", name: "Growth", price: "$17", sub: "/active client/mo",
    desc: "11–30 active clients. Scheduling, payroll, ATS, white-label.", badge: "Most popular" },
];

const STEPS = [
  { key: "agency", label: "Agency" },
  { key: "owner", label: "You" },
  { key: "plan", label: "Plan" },
  { key: "review", label: "Review" },
];

export default function AgencySignup() {
  const [params] = useSearchParams();
  const planParam = params.get("plan");
  const initialPlan = ["starter", "growth", "scale"].includes(planParam) ? planParam : "growth";

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    agency_name: "",
    npi: "",
    phone: "",
    owner_name: "",
    owner_email: "",
    owner_password: "",
    plan: initialPlan,
  });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  // When the API tells us the email is already registered, we switch into
  // the "check your inbox" state instead of erroring out.
  const [emailConfirmation, setEmailConfirmation] = useState(null); // { email }

  // Funnel: signup_started fires once, on first mount.
  useEffect(() => {
    track("signup_started", { initial_plan: initialPlan });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Funnel: signup_step_viewed each time the user lands on a new step.
  useEffect(() => {
    track("signup_step_viewed", { step_index: step, step_key: STEPS[step].key, plan: form.plan });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Per-step validation
  const canAdvance = useMemo(() => {
    if (step === 0) return form.agency_name.trim().length > 1;
    if (step === 1) return (
      form.owner_name.trim().length > 1 &&
      /.+@.+\..+/.test(form.owner_email) &&
      form.owner_password.length >= 8
    );
    if (step === 2) return !!form.plan;
    return true;
  }, [step, form]);

  const next = () => {
    if (!canAdvance) return;
    track("signup_step_advanced", { from_step: step, to_step: Math.min(step + 1, STEPS.length - 1), plan: form.plan });
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => {
    track("signup_step_back", { from_step: step });
    setStep((s) => Math.max(0, s - 1));
  };

  // On the final review step, submit creates the agency.
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/agencies/signup", {
        agency_name: form.agency_name.trim(),
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim().toLowerCase(),
        owner_password: form.owner_password,
        phone: form.phone.trim(),
      });

      // Existing-email path → switch into "check your inbox" state.
      if (data?.requires_email_confirmation) {
        track("signup_email_confirmation_sent", { email: data.email, agency_name: form.agency_name });
        setEmailConfirmation({ email: data.email });
        setSaving(false);
        return;
      }

      localStorage.setItem("hc_user_profile_cache", JSON.stringify(data.user));
      tagSentryUser(data.user);
      track("signup_completed", { agency_name: data.agency?.name, plan: form.plan });
      setDone(true);
      // Brief celebration before redirect
      setTimeout(() => {
        window.location.href = "/admin?onboarding=1";
      }, 1800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Could not create your agency. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-clinical-bg flex flex-col" data-testid="agency-signup-page">
      {/* Top bar */}
      <header className="border-b border-clinical-border/60 bg-white">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/"><AfyanexLogo markSize={28} /></Link>
          <Link to="/login" className="text-sm text-clinical-subtext hover:text-clinical-text" data-testid="signup-signin-link">
            Already have an account? <span className="font-medium text-clinical-primary">Sign in</span>
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center px-5 sm:px-8 py-10">
        <div className="w-full max-w-xl">
          {/* Stepper */}
          {!done && !emailConfirmation && (
            <ol className="mb-8 flex items-center justify-between gap-1" data-testid="signup-stepper">
              {STEPS.map((s, i) => (
                <li key={s.key} className="flex-1 flex items-center gap-1">
                  <div className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold transition ${
                    i < step ? "bg-clinical-primary text-white"
                    : i === step ? "bg-clinical-primary text-white ring-4 ring-clinical-primary/15"
                    : "bg-white border border-clinical-border text-clinical-subtext"
                  }`}>
                    {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </div>
                  <span className={`hidden sm:inline text-xs font-medium ${
                    i <= step ? "text-clinical-text" : "text-clinical-subtext"
                  }`}>
                    {s.label}
                  </span>
                  {i < STEPS.length - 1 && (
                    <span className={`flex-1 h-px ml-1 ${i < step ? "bg-clinical-primary" : "bg-clinical-border"}`} />
                  )}
                </li>
              ))}
            </ol>
          )}

          {/* Step content */}
          {done ? (
            <SuccessState />
          ) : emailConfirmation ? (
            <EmailConfirmationState email={emailConfirmation.email} agencyName={form.agency_name} />
          ) : (
            <div className="card-surface p-7 sm:p-8 shadow-[0_8px_30px_rgba(11,21,48,0.06)] rounded-2xl">
              {step === 0 && <StepAgency form={form} setForm={setForm} />}
              {step === 1 && <StepOwner form={form} setForm={setForm} />}
              {step === 2 && <StepPlan form={form} setForm={setForm} />}
              {step === 3 && <StepReview form={form} />}

              <div className="mt-7 pt-5 border-t border-clinical-border flex items-center justify-between">
                {step > 0 ? (
                  <button type="button" onClick={back}
                          className="btn-ghost text-sm" data-testid="signup-back">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                ) : <span />}
                {step < STEPS.length - 1 ? (
                  <button type="button" onClick={next}
                          disabled={!canAdvance}
                          className="btn-primary text-sm disabled:opacity-50"
                          data-testid="signup-next">
                    Continue <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button type="button" onClick={submit}
                          disabled={saving}
                          className="btn-primary text-sm disabled:opacity-50"
                          data-testid="signup-submit">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Create my agency
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Trust footer */}
          {!done && !emailConfirmation && (
            <div className="mt-5 text-center text-xs text-clinical-subtext flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> HIPAA-ready</span>
              <span>·</span>
              <span>14-day free trial</span>
              <span>·</span>
              <span>No credit card required</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ icon: Icon, children }) {
  return (
    <label className="label-eyebrow mb-1.5 flex items-center gap-1.5">
      {Icon && <Icon className="h-3 w-3" />} {children}
    </label>
  );
}

function StepAgency({ form, setForm }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="h-4 w-4 text-clinical-primary" />
        <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-clinical-primary">Step 1 of 4</span>
      </div>
      <h2 className="font-heading text-2xl font-semibold text-clinical-text tracking-tight">Tell us about your agency</h2>
      <p className="mt-1 text-sm text-clinical-subtext">We use this for branding, EVV records, and your family portal.</p>
      <div className="mt-6 space-y-4">
        <div>
          <FieldLabel icon={Building2}>Agency name</FieldLabel>
          <input value={form.agency_name}
                 onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
                 placeholder="Bright Home Care"
                 className="input-clinical"
                 data-testid="signup-agency-name"
                 required autoFocus />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <FieldLabel icon={Hash}>NPI number <span className="text-clinical-subtext">(optional)</span></FieldLabel>
            <input value={form.npi}
                   onChange={(e) => setForm({ ...form, npi: e.target.value })}
                   placeholder="10-digit NPI"
                   className="input-clinical"
                   data-testid="signup-npi" />
          </div>
          <div>
            <FieldLabel icon={Phone}>Phone</FieldLabel>
            <input value={form.phone}
                   onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   placeholder="(555) 123-4567"
                   className="input-clinical"
                   data-testid="signup-phone" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepOwner({ form, setForm }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <UserIcon className="h-4 w-4 text-clinical-primary" />
        <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-clinical-primary">Step 2 of 4</span>
      </div>
      <h2 className="font-heading text-2xl font-semibold text-clinical-text tracking-tight">Create your owner account</h2>
      <p className="mt-1 text-sm text-clinical-subtext">You'll be the agency administrator. We'll send your login here.</p>
      <div className="mt-6 space-y-4">
        <div>
          <FieldLabel icon={UserIcon}>Your full name</FieldLabel>
          <input value={form.owner_name}
                 onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                 placeholder="Alex Morgan"
                 className="input-clinical"
                 data-testid="signup-owner-name"
                 required autoFocus />
        </div>
        <div>
          <FieldLabel icon={Mail}>Work email</FieldLabel>
          <input type="email"
                 value={form.owner_email}
                 onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                 placeholder="alex@brighthomecare.com"
                 className="input-clinical"
                 data-testid="signup-owner-email"
                 required />
        </div>
        <div>
          <FieldLabel icon={Lock}>Password</FieldLabel>
          <input type="password"
                 value={form.owner_password}
                 onChange={(e) => setForm({ ...form, owner_password: e.target.value })}
                 placeholder="At least 8 characters"
                 className="input-clinical"
                 data-testid="signup-owner-password"
                 required />
          <p className="mt-1.5 text-[11px] text-clinical-subtext">
            Minimum 8 characters. You can enable 2FA after signup.
          </p>
        </div>
      </div>
    </div>
  );
}

function StepPlan({ form, setForm }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="h-4 w-4 text-clinical-primary" />
        <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-clinical-primary">Step 3 of 4</span>
      </div>
      <h2 className="font-heading text-2xl font-semibold text-clinical-text tracking-tight">Choose your plan</h2>
      <p className="mt-1 text-sm text-clinical-subtext">
        14 days free — no card required now. You can switch plans anytime.
      </p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="signup-plan-cards">
        {PLANS.map((p) => {
          const selected = form.plan === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => { setForm({ ...form, plan: p.key }); track("signup_plan_selected", { plan: p.key }); }}
              className={`relative text-left rounded-2xl p-5 transition ${
                selected
                  ? "bg-white border-2 border-clinical-primary shadow-[0_8px_30px_rgba(0,94,255,0.12)]"
                  : "bg-clinical-bg border border-clinical-border hover:border-clinical-primary/40"
              }`}
              data-testid={`signup-plan-${p.key}`}
            >
              {p.badge && (
                <span className="absolute -top-2.5 right-4 px-2 py-0.5 rounded-full bg-afyanex-pink text-white text-[10px] uppercase tracking-wider font-semibold">
                  {p.badge}
                </span>
              )}
              <div className="flex items-center justify-between">
                <div className="font-heading text-lg font-semibold text-clinical-text">{p.name}</div>
                {selected && <Check className="h-4 w-4 text-clinical-primary" />}
              </div>
              <div className="mt-1">
                <span className="font-heading text-2xl font-semibold text-clinical-text">{p.price}</span>
                <span className="text-xs text-clinical-subtext ml-1">{p.sub}</span>
              </div>
              <p className="mt-2 text-xs text-clinical-subtext leading-relaxed">{p.desc}</p>
            </button>
          );
        })}
      </div>
      <Link to="/pricing" target="_blank"
            className="mt-4 inline-block text-xs text-clinical-primary hover:underline">
        See full feature comparison →
      </Link>
    </div>
  );
}

function StepReview({ form }) {
  const plan = PLANS.find((p) => p.key === form.plan) || PLANS[1];
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-clinical-primary" />
        <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-clinical-primary">Step 4 of 4</span>
      </div>
      <h2 className="font-heading text-2xl font-semibold text-clinical-text tracking-tight">Confirm and create</h2>
      <p className="mt-1 text-sm text-clinical-subtext">Your free 14-day trial starts the moment you click create.</p>
      <div className="mt-6 rounded-2xl border border-clinical-border bg-clinical-bg/50 p-5 space-y-3"
           data-testid="signup-review-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-eyebrow">Agency</div>
            <div className="text-sm font-medium text-clinical-text">{form.agency_name}</div>
            <div className="text-xs text-clinical-subtext">{form.phone || "No phone provided"}</div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Plan</div>
            <div className="text-sm font-medium text-clinical-text">{plan.name}</div>
            <div className="text-xs text-clinical-subtext">{plan.price}{plan.sub}</div>
          </div>
        </div>
        <div className="border-t border-clinical-border/60 pt-3">
          <div className="label-eyebrow">Owner</div>
          <div className="text-sm font-medium text-clinical-text">{form.owner_name}</div>
          <div className="text-xs text-clinical-subtext">{form.owner_email}</div>
        </div>
      </div>
      <p className="mt-4 text-[11px] text-clinical-subtext leading-relaxed">
        By clicking <strong>Create my agency</strong>, you agree to our{" "}
        <Link to="/terms" target="_blank" className="text-clinical-primary hover:underline">Terms of Service</Link>{" "}
        and <Link to="/privacy" target="_blank" className="text-clinical-primary hover:underline">Privacy Policy</Link>.
        You'll be prompted to sign our HIPAA BAA before processing real patient data.
      </p>
    </div>
  );
}

function SuccessState() {
  return (
    <div className="card-surface p-10 text-center shadow-[0_8px_30px_rgba(11,21,48,0.06)] rounded-2xl"
         data-testid="signup-success-state">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
        <Check className="h-7 w-7" />
      </div>
      <h2 className="font-heading text-2xl font-semibold text-clinical-text tracking-tight">
        Your agency is live.
      </h2>
      <p className="mt-2 text-sm text-clinical-subtext">
        Setting things up — you'll land on the dashboard in a moment.
      </p>
      <div className="mt-5 flex items-center justify-center gap-2 text-xs text-clinical-subtext">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Launching your onboarding tour…
      </div>
    </div>
  );
}

function EmailConfirmationState({ email, agencyName }) {
  return (
    <div className="card-surface p-10 text-center shadow-[0_8px_30px_rgba(11,21,48,0.06)] rounded-2xl"
         data-testid="signup-email-confirmation-state">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-clinical-primary/10 text-clinical-primary">
        <Mail className="h-7 w-7" />
      </div>
      <h2 className="font-heading text-2xl font-semibold text-clinical-text tracking-tight">
        Check your inbox
      </h2>
      <p className="mt-3 text-sm text-clinical-subtext leading-relaxed">
        Your email <span className="font-medium text-clinical-text">{email}</span> is
        already linked to an Afyanex account. We've sent a confirmation link to
        verify and create <span className="font-medium text-clinical-text">{agencyName}</span> as
        an additional agency under your login.
      </p>
      <div className="mt-6 rounded-lg bg-clinical-bg border border-clinical-border p-4 text-left">
        <p className="text-xs font-semibold text-clinical-text mb-1.5">Next steps</p>
        <ol className="text-xs text-clinical-subtext leading-relaxed space-y-1 list-decimal list-inside">
          <li>Open the email from Afyanex Care (check spam if needed).</li>
          <li>Click <span className="font-medium text-clinical-text">"Confirm &amp; create agency"</span>.</li>
          <li>You'll be signed in to the new agency automatically.</li>
        </ol>
      </div>
      <p className="mt-5 text-xs text-clinical-subtext">
        Link expires in 60 minutes. Didn't get it?{" "}
        <Link to="/agency/signup" className="text-clinical-primary font-medium hover:underline">
          Try again
        </Link>.
      </p>
    </div>
  );
}

// Silence unused-import warning if dev tools strip these
export const _UNUSED = { Building2 };  // eslint-disable-line no-unused-vars
